import type { AuthenticatedUser } from '@iculogbook/shared';

import { db } from '../db.ts';

/**
 * The `meta` store: everything that is not an entry.
 *
 * Nothing here syncs. It is per-device state — which user this device last saw,
 * how far sync has got, whether the browser granted persistent storage.
 */

const KEYS = {
  lastPulledAt: 'lastPulledAt',
  lastExportAt: 'lastExportAt',
  deviceId: 'deviceId',
  currentUser: 'currentUser',
  storagePersisted: 'storagePersisted',
} as const;

async function read<T>(key: string): Promise<T | undefined> {
  const record = await db.meta.get(key);
  return record?.value as T | undefined;
}

async function write(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export const metaRepository = {
  /** Identifies this device to the sync engine. Created once, then reused. */
  async getDeviceId(): Promise<string> {
    const existing = await read<string>(KEYS.deviceId);
    if (existing !== undefined) return existing;

    const deviceId = crypto.randomUUID();
    await write(KEYS.deviceId, deviceId);
    return deviceId;
  },

  getLastPulledAt: () => read<string>(KEYS.lastPulledAt),
  setLastPulledAt: (iso: string) => write(KEYS.lastPulledAt, iso),

  /** Drives the 90-day backup reminder — hazard H1's control. */
  getLastExportAt: () => read<string>(KEYS.lastExportAt),
  setLastExportAt: (iso: string) => write(KEYS.lastExportAt, iso),

  /**
   * The last identity this device signed in as.
   *
   * Read when `/api/auth/me` cannot be reached, so a cold start with no signal
   * opens the logbook rather than a sign-in wall. Cleared on a real 401 and on
   * sign-out — this must never keep someone "signed in" after the server has
   * said otherwise.
   */
  getCurrentUser: () => read<AuthenticatedUser>(KEYS.currentUser),
  setCurrentUser: (user: AuthenticatedUser) => write(KEYS.currentUser, user),
  clearCurrentUser: () => db.meta.delete(KEYS.currentUser),

  /**
   * Whether the browser granted persistent storage. Recorded so phase 13 can
   * prompt the user to install to the home screen when it was refused — that
   * is the H3 control, against iOS evicting a non-installed PWA's data after
   * seven days.
   */
  getStoragePersisted: () => read<boolean>(KEYS.storagePersisted),
  setStoragePersisted: (persisted: boolean) => write(KEYS.storagePersisted, persisted),
};
